import { useEffect, useState } from 'react';
import { Box, List, ListItemButton, ListItemText, MenuItem, Typography } from '@mui/material';
import Menu from '@mui/material/Menu';
import { useTranslation } from 'react-i18next';
import ImageButton from '../ImageButton/ImageButton';
import { useAppDispatch } from '../../redux/hooks';
import { languageUpdate } from '../../redux/coach/coachSlice';

const languageOptions = [
  {
    id: 'en',
    text: 'EN',
    flag: '/assets/icons/circle-flags-uk.svg',
  },
  {
    id: 'pt',
    text: 'PT',
    flag: '/assets/icons/flag-for-portugal.svg',
  },
  {
    id: 'ar',
    text: 'AR',
    flag: '/assets/icons/flag-for-arabic.svg',
  },
];

const LanguageSwitcher = () => {
  const dispatch = useAppDispatch();
  const { i18n } = useTranslation();
  const currentLanguageIndex = languageOptions.findIndex((lng) => lng.id === i18n.language);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(
    currentLanguageIndex === -1 ? 0 : currentLanguageIndex
  );
  const open = Boolean(anchorEl);

  const handleClickLngSwitcherTrigger = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageSwitch = (_event: React.MouseEvent<HTMLElement>, index: number) => {
    if (index === selectedIndex) {
      return;
    }
    i18n.changeLanguage(languageOptions[index].id);
    setSelectedIndex(index);
    dispatch(languageUpdate({ language: languageOptions[index].id }));
    setAnchorEl(null);
  };

  useEffect(() => {
    if (languageOptions[selectedIndex].id === 'ar') {
      document.documentElement.dir = 'rtl';
    } else {
      document.documentElement.dir = 'ltr';
    }
  }, [selectedIndex]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <List component="nav" aria-label="Select language">
        <ListItemButton
          id="language-switcher-button"
          aria-haspopup="listbox"
          aria-controls="languages-menu"
          aria-label="Language Swicher"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClickLngSwitcherTrigger}
          sx={{
            padding: 0,
          }}
        >
          <ListItemText
            primary={
              <Box display="flex" flexDirection="row" alignItems="center" gap="4px">
                <img
                  loading="lazy"
                  srcSet={`${languageOptions[selectedIndex].flag} 2x`}
                  src={languageOptions[selectedIndex].flag}
                  alt={`${languageOptions[selectedIndex].text} flag`}
                  height={24}
                  width={24}
                />
                <Typography variant="calloutRegular" color="primary">
                  {languageOptions[selectedIndex].text}
                </Typography>
                <ImageButton src="/assets/icons/alt-arrow-down.svg" alt="chevron-down" />
              </Box>
            }
          />
        </ListItemButton>
      </List>
      <Menu
        id="languages-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'language-switcher-button',
          role: 'listbox',
        }}
      >
        {languageOptions.map((option, index) => (
          <MenuItem
            key={option.id}
            selected={index === selectedIndex}
            onClick={(event) => handleLanguageSwitch(event, index)}
          >
            <img
              loading="lazy"
              width="20"
              srcSet={`${option.flag} 2x`}
              src={option.flag}
              alt={`${option.text} flag`}
              style={{ marginRight: '10px' }}
            />
            {option.text}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export default LanguageSwitcher;
